/**
 * Issue owners — the issue-triage developers an issue can be assigned to.
 *
 * The list comes from the server (issueTriage_listOwners), which resolves the
 * configured developer logins to live users records. The client never reads
 * the users collection for this and never sends a name — only a uid the
 * server re-checks against that same list.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { httpsCallable } from "../../lib/firebase/authReadyCallable";
import { definedFields } from "../../lib/firebase/definedFields";
import { Timestamp } from "firebase/firestore";
import { functions } from "../../config/firebase";
import { invokeNamedCallable } from "../../lib/telemetry/namedCallable";
import { useAuth } from "../../contexts/AuthContext";
import { signedInAsIssuePerson } from "../../lib/issueTriage/issuePerson";
import { statusAfterEdit } from "../../lib/issueTriage/reviewRule";
import { issueReportKeys } from "./useIssueReports";
import { issueOwnerKeys } from "./issueOwnerKeys";
import type { IssuePerson, IssueReport } from "../../types/issue-triage";

export { issueOwnerKeys };

/** The assignable developers, as {uid, name}. Changes only with a deploy. */
export function useIssueOwners() {
  const { user } = useAuth();

  return useQuery({
    queryKey: issueOwnerKeys.list(),
    queryFn: async (): Promise<IssuePerson[]> => {
      const fn = httpsCallable<{ organizationId?: string }, { owners: IssuePerson[] }>(
        functions,
        "issueTriage_listOwners"
      );
      const res = await invokeNamedCallable("issueTriage_listOwners", () =>
        fn({ organizationId: user?.organizationId })
      );
      return Array.isArray(res.data?.owners) ? res.data.owners : [];
    },
    enabled: !!user,
    staleTime: 60 * 60 * 1000,
  });
}

interface SetOwnerVars {
  issueId: string;
  /** null clears the owner. */
  owner: IssuePerson | null;
  /** The owner uid the caller saw ("" = none); the server refuses if a colleague has changed it since. */
  expectedOwnerUid?: string;
}

/**
 * Set or clear an issue's owner. Optimistic: the card/panel/filter move at
 * once; the server re-validates the uid and stamps who changed it.
 */
export function useSetIssueOwner() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ issueId, owner, expectedOwnerUid }: SetOwnerVars) => {
      if (!user) {
        throw new Error("You must be logged in to change an issue's owner");
      }
      const fn = httpsCallable(functions, "issueTriage_setOwner");
      await invokeNamedCallable("issueTriage_setOwner", () =>
        fn(definedFields({ organizationId: user.organizationId, issueId, ownerUid: owner?.uid ?? "", expectedOwnerUid }))
      );
      return { issueId };
    },
    onMutate: async ({ issueId, owner }) => {
      await queryClient.cancelQueries({ queryKey: issueReportKeys.all });
      const previousLists = queryClient.getQueriesData({ queryKey: issueReportKeys.lists() });
      const by = signedInAsIssuePerson(
        user,
        queryClient.getQueryData<IssuePerson[]>(issueOwnerKeys.list())
      );

      queryClient.setQueriesData(
        { queryKey: issueReportKeys.lists() },
        (old: IssueReport[] | undefined) => {
          if (!old) return old;
          return old.map((issue) => {
            if (issue.id !== issueId) return issue;
            // Same owner: the server writes nothing, so neither do we.
            if ((issue.owner?.uid ?? "") === (owner?.uid ?? "")) return issue;
            const next = { ...issue, owner: owner ?? undefined };
            // Same rule as the server: promote when ready, demote when not.
            return {
              ...next,
              ownerUpdatedBy: by,
              ownerUpdatedAt: Timestamp.now(),
              status: statusAfterEdit(next),
            };
          });
        }
      );

      return { previousLists };
    },
    onError: (_err, _vars, context) => {
      context?.previousLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: issueReportKeys.all });
    },
  });
}
